import { styled, alpha, Chip, Modal, Box, Skeleton } from '@mui/material';
import Stack from '@mui/material/Stack';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { Button } from '@/components/Button';

export const ListStack = styled(Stack)`
  gap: 8px;
  width: 100%;
  max-width: 95%;
  margin: 0 auto;
  padding: 8px;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 4px;
  }
`;

export const CardStack = styled(Stack)`
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

//  Search Panel
export const SearchPanel = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;

  @media (max-width: 600px) {
    gap: 16px;
  }
`;

export const SearchRow = styled('div')`
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: center;
  padding: 4px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? theme.palette.transparent.tertiary
      : theme.palette.transparent.quaternary};

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

export const FilterRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const FilterGroup = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

//  Status Filter Toggle
export const StatusFilterGroup = styled(ToggleButtonGroup)`
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.grey['02']
        : theme.palette.primary.main};
  height: 42px;

  .MuiToggleButtonGroup-grouped {
    border: none;
    &:not(:first-of-type) {
      border-left: 1px solid
        ${({ theme }) =>
          theme.palette.mode === 'dark'
            ? theme.palette.transparent.tertiary
            : theme.palette.primary.main};
    }
  }
`;

export const StatusToggleButton = styled(ToggleButton)`
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.palette.primary.main};
  transition: all 0.25s ease;
  white-space: nowrap;

  @media (max-width: 600px) {
    padding: 6px 10px;
    font-size: 11px;
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 700;
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};
  }

  &.Mui-selected:hover {
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.18)};
  }

  &:hover {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.transparent.quaternary};
  }
`;

//  Display Mode Toggle
export const DisplayModeGroup = styled(ToggleButtonGroup)`
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.primary.main};
  height: 36px;

  .MuiToggleButtonGroup-grouped {
    border: none;
    &:not(:first-of-type) {
      border-left: 1px solid
        ${({ theme }) =>
          theme.palette.mode === 'dark'
            ? theme.palette.transparent.tertiary
            : theme.palette.primary.main};
    }
  }
`;

export const DisplayToggleButton = styled(ToggleButton)`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 400;
  text-transform: none;
  color: ${({ theme }) => theme.palette.primary.main};
  transition: all 0.25s ease;
  white-space: nowrap;

  @media (max-width: 600px) {
    padding: 6px 10px;
    font-size: 11px;
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.primary.main};
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};
    font-weight: 700;
  }

  &.Mui-selected:hover {
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.18)};
  }

  &:hover {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.transparent.quaternary};
  }
`;

//  Header
export const HeaderContent = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  flex-wrap: wrap;
`;

export const HeaderActionsBox = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const FilterActionsBox = styled(Box)`
  display: flex;
  gap: 16px;
  align-self: auto;

  @media (max-width: 600px) {
    align-self: flex-end;
  }
`;

export const SearchAndFilterStack = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 18px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TitleStack = styled(Stack)`
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

//  Status components (exported, used by other pages)
interface StatusProps {
  online: boolean;
}

export const StatusTag = styled('div')<StatusProps>`
  color: ${(props) => (props.online ? '#0fcc45' : '#cc0f0fff')};
  border-color: ${(props) => (props.online ? '#0fcc45' : '#cc0f0fff')};
  border-radius: 32px;
  border: 1px solid;
  padding: 4px 2px 4px 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  text-transform: uppercase;
  overflow: hidden;
  height: 32px;
  min-width: 110px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.online ? '#0fcc45' : '#cc0f0fff')};
    color: white;
  }
`;

export const StatusBall = styled('div')<StatusProps>`
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 10px;

  background-color: ${(props) => (props.online ? '#0fcc45' : '#cc0f0fff')};
  border-radius: 50%;

  position: relative;

  .blink {
    display: block;
    width: 12px;
    height: 12px;

    background-color: ${(props) => (props.online ? '#0fcc45' : '#cc0f0fff')};
    opacity: 0.7;
    border-radius: 50%;

    animation: blink 1s linear infinite;
  }

  /*Animations*/

  @keyframes blink {
    100% {
      transform: scale(2, 2);
      opacity: 0;
    }
  }
`;

export const StatusChip = styled(Chip)<{ value: boolean }>`
  border-radius: 4px;
  background-color: ${({ value, theme }) =>
    value ? theme.palette.primary.main : theme.palette.grey['01']};
  color: ${({ value, theme }) =>
    value ? theme.palette.primary.contrastText : theme.palette.fixed.white};
  height: 24px;
  font-weight: 600;
`;

export const ModeEditionChip = styled(Chip)`
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.primary['01'] + '50'};
  color: ${({ theme }) => theme.palette.primary['01']};
  border: 1px solid ${({ theme }) => theme.palette.primary['01']};
  height: 32px;
  font-weight: 600;
`;

export const MassiveActionButton = styled(Button)`
  height: 36px;
  width: 36px;
  padding: 2px 2px;
  min-width: 0px;
  background-color: ${({ theme }) => theme.palette.primary['01'] + '40'};
  color: ${({ theme }) => theme.palette.primary['01']};
`;

export const EnableDevicesButton = styled(Button)`
  text-transform: none;
  justify-content: flex-start;
  padding: 6px auto;
  background-color: ${({ theme }) => theme.palette.secondary['01']};
  color: ${({ theme }) => theme.palette.white['01']};

  :hover {
    background-color: ${({ theme }) => theme.palette.secondary['01'] + 'CC'};
  }
`;

export const DisableDevicesButton = styled(Button)`
  text-transform: none;
  justify-content: flex-start;
  padding: 6px auto;
  background-color: ${({ theme }) => theme.palette.status.offline};
  color: ${({ theme }) => theme.palette.white['06']};

  :hover {
    background-color: ${({ theme }) => theme.palette.status.offline + 'CC'};
  }
`;

export const BulkActionModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;

  muibackdrop-root {
    backdrop-filter: blur(6px);
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

export const ButtonsBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CancelButtonBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

export const ResultCardSkeleton = styled(Skeleton)`
  width: 320px;
  height: 200px;
  border-radius: 20px;
`;

export const OutlinedButton = styled(Button)<{ variant?: string }>`
  text-transform: none;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.palette.secondary['01']};
  color: ${({ theme }) => theme.palette.secondary['01']};

  :hover {
    background-color: ${({ theme }) => theme.palette.secondary['01'] + 'CC'};
    color: ${({ theme }) => theme.palette.white['01']};
    border-color: ${({ theme }) => theme.palette.secondary['01'] + 'CC'};
  }
`;

export const FilterActionButton = styled(Button)`
  text-transform: none;
  height: 42px;
  border: ${({ theme }) =>
    theme.palette.mode === 'light'
      ? `1px solid ${theme.palette.primary.main}`
      : 'none'};
`;
